import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Card, CardBody, Row, Col, Button } from 'reactstrap'
import ApptReqConfTableContainer from 'components/Common/ApptReqConfTableContainer'
import AddressMappingContext from './AddressMappingContext'
import NewJobWizard from './NewJobWizard'

const STATUS_BADGE = {
    PENDING_UPLOAD:  'secondary',
    PENDING_MAPPING: 'info',
    PROCESSING:      'warning',
    COMPLETE:        'success',
    ERROR:           'danger',
}

function formatBytes(bytes) {
    if (bytes == null) return '-'
    if (bytes < 1024)          return bytes + ' B'
    if (bytes < 1024 * 1024)  return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatDate(dt) {
    if (!dt) return '-'
    return new Date(dt).toLocaleString()
}

function JobList() {
    const { records, loading, fetchRecords, refreshDownloadUrl } = useContext(AddressMappingContext)
    const [showWizard, setShowWizard] = useState(false)

    const handleDownload = useCallback(async (url, expires, controlId) => {
        const isExpired = expires && new Date(expires) < new Date()
        if (!isExpired) {
            window.open(url, '_blank', 'noreferrer')
            return
        }
        try {
            const freshUrl = await refreshDownloadUrl(controlId)
            if (freshUrl) window.open(freshUrl, '_blank', 'noreferrer')
        } catch (e) {
            console.error('Failed to refresh download URL', e)
        }
    }, [refreshDownloadUrl])

    useEffect(() => {
        fetchRecords()
    }, [])

    const columns = useMemo(() => [
        {
            accessor: 'job_name',
            Header: 'Reference',
            Cell: ({ value }) => value || '-',
        },
        {
            accessor: 'file_name',
            Header: 'File Name',
            Cell: ({ value }) => value || '-',
        },
        {
            accessor: 'file_size',
            Header: 'File Size',
            Cell: ({ value }) => formatBytes(value),
        },
        {
            accessor: 'total_records',
            Header: 'Records',
            Cell: ({ row }) => {
                const total = row.original.total_records
                const processed = row.original.processed_records
                if (total == null) return <span className="text-muted">Pending</span>
                return `${total}`
            },
        },
        {
            accessor: 'created_at',
            Header: 'Created On',
            Cell: ({ value }) => formatDate(value),
        },
        {
            accessor: 'status',
            Header: 'Status',
            Cell: ({ value }) => (
                <span className={`badge bg-${STATUS_BADGE[value] || 'secondary'}`}>
                    {value || '-'}
                </span>
            ),
        },
        {
            accessor: 'output_presigned_url',
            Header: 'Output File',
            Cell: ({ row }) => {
                const url       = row.original.output_presigned_url
                const expires   = row.original.output_url_expires_at
                const controlId = row.original.job_id
                if (!url) return '-'
                return (
                    <a
                        href="#"
                        onClick={e => { e.preventDefault(); handleDownload(url, expires, controlId) }}
                    >
                        Download
                    </a>
                )
            },
        },
    ], [])

    return (
        <>
            <Card>
                <CardBody>
                    {/* Title + New Upload button merged into the table card */}
                    <Row className="mb-3 align-items-center">
                        <Col>
                            <h5 className="text-primary mb-0">Address Mapping Analysis</h5>
                        </Col>
                        <Col className="text-end">
                            <Button color="primary" size="sm" onClick={() => setShowWizard(true)}>
                                <i className="bx bx-plus me-1" />
                                New Upload
                            </Button>
                        </Col>
                    </Row>

                    <ApptReqConfTableContainer
                        columns={columns}
                        data={records}
                        isGlobalFilter={true}
                        isAddOptions={true}
                        customPageSize={20}
                        loading={loading}
                        refreshTable={() => fetchRecords()}
                        className="custom-header-css"
                    />
                </CardBody>
            </Card>

            {showWizard && (
                <NewJobWizard
                    onClose={() => setShowWizard(false)}
                    onComplete={() => {
                        setShowWizard(false)
                        fetchRecords()
                    }}
                />
            )}
        </>
    )
}

export default JobList
